import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import type { AuthUser } from '../lib/api.ts';

/* 单色线性图标 —— 16px，stroke 跟随文字色 */
function Icon({ d }: { d: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d={d} stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const ICONS = {
  meetings:
    'M2.5 3.5h11v10h-11zM2.5 6.5h11M5.5 2v2.5M10.5 2v2.5M5.25 9h1.5M7.75 9h1.5M10.25 9h.5M5.25 11.25h1.5M7.75 11.25h1.5',
  admin: 'M3 5h10M3 11h10M6 3v4M10 9v4',
  settings:
    'M8 5.2A2.8 2.8 0 1 0 8 10.8 2.8 2.8 0 0 0 8 5.2zM8 1.5l.4 1.8 1.7-.7 1.3 1.3-.7 1.7 1.8.4v1.8l-1.8.4.7 1.7-1.3 1.3-1.7-.7-.4 1.8H7.6l-.4-1.8-1.7.7-1.3-1.3.7-1.7-1.8-.4V6l1.8-.4-.7-1.7L6.4 2.6l1.7.7z',
  password: 'M3.5 7.5h9v6h-9zM5.5 7.5v-2a2.5 2.5 0 0 1 5 0v2',
  signout: 'M6 2.5H3.5v11H6M10.5 5l3 3-3 3M13 8H6.5',
  collapse: 'M9.5 5 6 8.5 9.5 12M13.5 5 10 8.5l3.5 3.5',
  chevron: 'M5 6.2 8 9.2l3-3',
  account: 'M8 3a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5zM3 13.5c0-2.5 2.2-4 5-4s5 1.5 5 4',
  directory:
    'M6 2.5v11M10 2.5v11M2.5 6h2.5M2.5 10h2.5M11 6h2.5M11 10h2.5',
  feishuDirectory:
    'M8 2a3 3 0 1 1 0 6 3 3 0 0 1 0-6zM3 14c0-2.5 2.2-4 5-4s5 1.5 5 4M11.5 4.5l1 1M14 3.5l-1.5 1.5',
};

/* ── 侧栏导航模型 ─────────────────────────────────────────
 * 叶子项直接导航；组项点击只展开/收起（导航由子项承担），
 * 但收起模式下组项点击直达默认子路由 —— 窄栏里没有子菜单可点。
 */
interface NavChild {
  readonly to: string;
  readonly label: string;
  readonly active: boolean;
}

type NavEntry =
  | { readonly kind: 'leaf'; readonly key: string; readonly to: string; readonly label: string; readonly icon: string }
  | {
      readonly kind: 'group';
      readonly key: string;
      readonly to: string;
      readonly label: string;
      readonly icon: string;
      readonly match: readonly string[];
      readonly children: readonly NavChild[];
    };

const SETTINGS_NAV: readonly { key: string; title: string }[] = [
  { key: 'general', title: '通用' },
  { key: 'jobs', title: '定时任务' },
  { key: 'integrations', title: '集成凭据' },
  { key: 'audit', title: '审计日志' },
];

function buildNav(
  perms: readonly string[],
  adminTab: string,
  pathname: string,
): readonly NavEntry[] {
  const list: NavEntry[] = [
    { kind: 'leaf', key: 'meetings', to: '/meetings', label: '会议', icon: ICONS.meetings },
  ];
  // 腾讯通讯录：管理者视角（与 MCP 工具同一权限点）
  if (perms.includes('tencent:user:list')) {
    list.push({
      kind: 'leaf',
      key: 'directory',
      to: '/directory',
      label: '腾讯通讯录',
      icon: ICONS.directory,
    });
  }
  // 飞书通讯录：管理者视角（与 MCP 工具同一权限点）
  if (perms.includes('feishu:user:list')) {
    list.push({
      kind: 'leaf',
      key: 'feishu-directory',
      to: '/feishu-directory',
      label: '飞书通讯录',
      icon: ICONS.feishuDirectory,
    });
  }
  const canUsers = perms.includes('user:manage');
  const canRoles = perms.includes('role:manage');
  const canDepts = perms.includes('department:manage');
  if (canUsers || canRoles || canDepts) {
    // 子项路径相同（/admin），靠 ?tab= 区分 —— 激活判定必须自己算。
    // 只渲染有权限的那几个：没有 department:manage 的人不该看到「部门」这个死入口。
    const children = [
      ...(canUsers
        ? [
            {
              to: '/admin?tab=users',
              label: '账号',
              active: pathname.startsWith('/admin') && adminTab === 'users',
            },
          ]
        : []),
      ...(canRoles
        ? [
            {
              to: '/admin?tab=roles',
              label: '角色与权限',
              active: pathname.startsWith('/admin') && adminTab === 'roles',
            },
          ]
        : []),
      ...(canDepts
        ? [
            {
              to: '/admin?tab=departments',
              label: '部门',
              active: pathname.startsWith('/admin') && adminTab === 'departments',
            },
          ]
        : []),
    ];
    list.push({
      kind: 'group',
      key: 'admin',
      to: '/admin',
      label: '用户管理',
      icon: ICONS.admin,
      match: ['admin'],
      children,
    });
  }
  if (perms.includes('config:manage')) {
    list.push({
      kind: 'group',
      key: 'settings',
      to: '/settings/general',
      label: '系统管理',
      icon: ICONS.settings,
      match: ['settings'],
      children: SETTINGS_NAV.map((s) => ({
        to: `/settings/${s.key}`,
        label: s.title,
        active: pathname === `/settings/${s.key}`,
      })),
    });
  }
  return list;
}

/* localStorage 在隐私模式下会直接抛异常，而「上次展开哪一组」
   只是使用习惯，不值当让整个界面崩掉。 */
const SIDE_KEY = 'upoa_side';
const OPEN_KEY = 'upoa_side_open';

function readStore(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function writeStore(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* 隐私模式，忽略 */
  }
}

interface SidebarProps {
  readonly perms: readonly string[];
  readonly me: AuthUser | null;
  readonly onSignOut: () => void;
  readonly onPassword: () => void;
}

/**
 * 侧栏：208px 展开 / 48px 收起的图标栏，两级菜单（分组 → 页）。
 * 收起态的二级菜单变成 hover 浮层、单开手风琴、窄屏自动收起但不写回偏好。
 */
export function Sidebar({ perms, me, onSignOut, onPassword }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // ?tab= 缺省是 users（Admin 组件里也这么兜底），两边必须一致否则高亮会飘
  const rawTab = searchParams.get('tab');
  const adminTab: string = rawTab === 'roles' || rawTab === 'departments' ? rawTab : 'users';
  const nav = buildNav(perms, adminTab, location.pathname);

  // 窄屏自动收起 vs 用户手动收起：两个独立的量。窄屏收起不覆盖用户偏好。
  const [autoCollapsed, setAutoCollapsed] = useState(false);
  const [userCollapsed, setUserCollapsed] = useState(() => readStore(SIDE_KEY) === '1');
  const collapsed = userCollapsed || autoCollapsed;
  const toggleCollapsed = (): void => {
    const next = !userCollapsed;
    setUserCollapsed(next);
    writeStore(SIDE_KEY, next ? '1' : '0');
  };
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1024px)');
    setAutoCollapsed(mq.matches);
    const onChange = (e: MediaQueryListEvent): void => setAutoCollapsed(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // 取路径第一段而不是整条路径：详情地址比菜单键多一段，用整条会匹配不上
  const pathSeg = location.pathname.split('/')[1] || '';
  const currentGroup = nav.find((n) => n.kind === 'group' && n.match.includes(pathSeg))?.key;

  // 单开手风琴：openGroup 是单个组名。当前路由所在组自动展开 ——
  // 必须用 ref 记住「上次自动展开过谁」，否则手动展开别的组会被
  // 「路由还在旧组」的自动逻辑立刻撤销（点了没反应，不报错不动画）。
  const [openGroup, setOpenGroup] = useState<string>(() => readStore(OPEN_KEY) ?? '');
  const lastAutoGroup = useRef('');
  useEffect(() => {
    if (currentGroup !== undefined && lastAutoGroup.current !== currentGroup) {
      lastAutoGroup.current = currentGroup;
      setOpenGroup(currentGroup);
      writeStore(OPEN_KEY, currentGroup);
    }
  }, [currentGroup]);
  const onParentToggle = (key: string): void => {
    const next = openGroup === key ? '' : key;
    setOpenGroup(next);
    writeStore(OPEN_KEY, next);
  };

  /* 用户菜单：点用户面板弹出，点外部或按 Esc 关闭。 */
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const footRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!userMenuOpen) return;
    const onDown = (e: PointerEvent): void => {
      if (footRef.current && !footRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setUserMenuOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [userMenuOpen]);

  return (
    <aside className={`sidebar${collapsed ? ' collapsed' : ''}`}>
      <div className="side-brand">
        <img src="/logo.png" alt="UP-OA" className="brand-logo" />
        <span className="brand-text">UP-OA</span>
        {/* 收起态的 48px 栏里，品牌与这个按钮竖排（并排放不下） */}
        <button
          type="button"
          className="side-collapse"
          onClick={toggleCollapsed}
          aria-label={collapsed ? '展开菜单' : '收起菜单'}
          data-label={collapsed ? '展开菜单' : '收起菜单'}
        >
          <Icon d={ICONS.collapse} />
        </button>
      </div>

      <nav className="side-nav">
        {nav.map((item) => {
          if (item.kind === 'leaf') {
            return (
              <NavLink
                key={item.key}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) => (isActive ? 'on' : '')}
                // 收起态标签文字 display:none 会一起从无障碍树里消失
                aria-label={collapsed ? item.label : undefined}
                data-label={collapsed ? item.label : undefined}
              >
                <Icon d={item.icon} />
                <span className="nav-label">{item.label}</span>
              </NavLink>
            );
          }
          const open = openGroup === item.key;
          const subId = `nav-sub-${item.key}`;
          return (
            // open 类是手风琴的开关（子菜单高度 0fr → 1fr + chevron 转向）
            <div className={`nav-group${open && !collapsed ? ' open' : ''}`} key={item.key}>
              <button
                type="button"
                className={`nav-parent${currentGroup === item.key ? ' on' : ''}`}
                aria-expanded={collapsed ? undefined : open}
                aria-controls={collapsed ? undefined : subId}
                aria-haspopup={collapsed ? true : undefined}
                aria-label={collapsed ? item.label : undefined}
                onClick={() => (collapsed ? navigate(item.to) : onParentToggle(item.key))}
              >
                <Icon d={item.icon} />
                <span className="nav-label">{item.label}</span>
                <span className="nav-chevron">
                  <Icon d={ICONS.chevron} />
                </span>
              </button>
              {/* 收起态渲染它才有 hover 浮层可弹；关合的展开态用 inert 摘出焦点顺序 */}
              <div
                className="nav-sub-wrap"
                id={subId}
                inert={!collapsed && !open ? true : undefined}
              >
                <div className="nav-sub">
                  {item.children.map((c) => (
                    <Link
                      key={c.to}
                      to={c.to}
                      className={c.active ? 'on' : ''}
                      aria-current={c.active ? 'page' : undefined}
                    >
                      {c.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </nav>

      <div className="side-foot" ref={footRef}>
        {me !== null && (
          <>
            <button
              type="button"
              className={`side-user-btn${userMenuOpen ? ' on' : ''}`}
              aria-expanded={userMenuOpen}
              aria-haspopup="menu"
              aria-label={`${me.displayName}菜单`}
              onClick={() => setUserMenuOpen((v) => !v)}
            >
              <span className="avatar">{me.displayName.slice(0, 1)}</span>
              <span className="who">
                <b>{me.displayName}</b>
                <small>{me.role}</small>
              </span>
              <span className="user-menu-chevron">
                <Icon d={ICONS.chevron} />
              </span>
            </button>
            {userMenuOpen && (
              <div className="user-menu" role="menu">
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setUserMenuOpen(false);
                    onPassword();
                  }}
                >
                  <Icon d={ICONS.password} />
                  <span>修改密码</span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setUserMenuOpen(false);
                    window.location.assign('/account');
                  }}
                >
                  <Icon d={ICONS.account} />
                  <span>账号安全</span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setUserMenuOpen(false);
                    onSignOut();
                  }}
                >
                  <Icon d={ICONS.signout} />
                  <span>退出登录</span>
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </aside>
  );
}
